/**
 * Core Timeline Component — Composable, step-aware timeline.
 *
 * Clean flexbox layout — no absolute positioning, no magic offsets.
 * Each item = [indicator column (dot + line)] | [content column]
 *
 * Usage:
 *   <Timeline value={3}>
 *     <TimelineItem step={1}>
 *       <TimelineIndicator><Check /></TimelineIndicator>
 *       <TimelineHeader>
 *         <TimelineTitle>Step title</TimelineTitle>
 *       </TimelineHeader>
 *       <TimelineContent>Body text</TimelineContent>
 *     </TimelineItem>
 *   </Timeline>
 *
 * Also exports <SimpleTimeline items={[...]} /> for quick usage.
 */
import React, { createContext, useContext } from 'react';
import { cn } from '@/lib/utils';

// ─── Context ────────────────────────────────────────────────────────────────

interface TimelineContextValue {
  value: number;
}

const TimelineContext = createContext<TimelineContextValue>({
  value: 0,
});

function useTimeline() {
  return useContext(TimelineContext);
}

// ─── Timeline (root) ────────────────────────────────────────────────────────

export interface TimelineProps {
  children: React.ReactNode;
  /** The step number that is currently active */
  value?: number;
  /** For uncontrolled usage */
  defaultValue?: number;
  className?: string;
}

export const Timeline: React.FC<TimelineProps> = ({
  children,
  value,
  defaultValue = 0,
  className,
}) => {
  const activeValue = value ?? defaultValue;

  return (
    <TimelineContext.Provider value={{ value: activeValue }}>
      <div className={cn('flex flex-col', className)}>
        {children}
      </div>
    </TimelineContext.Provider>
  );
};

// ─── TimelineItem ───────────────────────────────────────────────────────────

export interface TimelineItemProps {
  children: React.ReactNode;
  step: number;
  /** Whether this is the last item (hides the connector line) */
  isLast?: boolean;
  className?: string;
}

export const TimelineItem: React.FC<TimelineItemProps> = ({
  children,
  step,
  isLast = false,
  className,
}) => {
  const { value } = useTimeline();
  const status = step < value ? 'completed' : step === value ? 'active' : 'pending';

  // Separate indicator from other children
  let indicator: React.ReactNode = null;
  const contentChildren: React.ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === TimelineIndicator) {
      indicator = child;
    } else {
      contentChildren.push(child);
    }
  });

  return (
    <div
      className={cn('group/timeline-item flex gap-3', className)}
      data-status={status}
      data-step={step}
    >
      {/* Left column: indicator dot + connector line */}
      <div className="flex flex-col items-center shrink-0" style={{ width: 28 }}>
        {indicator || <TimelineIndicator />}
        {!isLast && (
          <div
            className={cn(
              'w-px flex-1 mt-1.5',
              'bg-slate-200 dark:bg-slate-700',
              status === 'completed' && 'bg-[var(--primary-color)]',
            )}
          />
        )}
      </div>

      {/* Right column: header + content */}
      <div className={cn('flex-1 min-w-0', !isLast ? 'pb-6' : 'pb-1')}>
        {contentChildren}
      </div>
    </div>
  );
};

// ─── TimelineIndicator ──────────────────────────────────────────────────────

export interface TimelineIndicatorProps {
  children?: React.ReactNode;
  className?: string;
}

export const TimelineIndicator: React.FC<TimelineIndicatorProps> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'flex items-center justify-center rounded-full shrink-0',
      'w-7 h-7',
      'border-2 border-white dark:border-slate-900',
      // Default (pending)
      'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
      // Completed — filled primary
      'group-data-[status=completed]/timeline-item:bg-[var(--primary-color)] group-data-[status=completed]/timeline-item:text-white group-data-[status=completed]/timeline-item:border-[var(--primary-color)]/20',
      // Active — outlined primary
      'group-data-[status=active]/timeline-item:border-[var(--primary-color)] group-data-[status=active]/timeline-item:bg-[var(--primary-color)]/10 group-data-[status=active]/timeline-item:text-[var(--primary-color)]',
      className,
    )}
  >
    {children}
  </div>
);

// ─── TimelineSeparator (kept for backward compat, but line is now in TimelineItem) ───

export interface TimelineSeparatorProps {
  className?: string;
}

export const TimelineSeparator: React.FC<TimelineSeparatorProps> = ({ className }) => (
  <div className={cn('w-px flex-1 bg-slate-200 dark:bg-slate-700', className)} />
);

// ─── TimelineHeader ─────────────────────────────────────────────────────────

export interface TimelineHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const TimelineHeader: React.FC<TimelineHeaderProps> = ({
  children,
  className,
}) => (
  <div className={cn('flex items-center gap-2 min-h-[28px]', className)}>
    {children}
  </div>
);

// ─── TimelineTitle ──────────────────────────────────────────────────────────

export interface TimelineTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const TimelineTitle: React.FC<TimelineTitleProps> = ({
  children,
  className,
}) => (
  <h4 className={cn('text-sm font-semibold text-slate-800 dark:text-slate-200', className)}>
    {children}
  </h4>
);

// ─── TimelineContent ────────────────────────────────────────────────────────

export interface TimelineContentProps {
  children: React.ReactNode;
  className?: string;
}

export const TimelineContent: React.FC<TimelineContentProps> = ({
  children,
  className,
}) => (
  <div className={cn('text-sm text-slate-500 dark:text-slate-400 mt-1', className)}>
    {children}
  </div>
);

// ─── SimpleTimeline (backward-compatible) ───────────────────────────────────
// Quick-use version for audit logs, activity feeds, etc.

export interface SimpleTimelineItem {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  timestamp?: string;
  status?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'pending';
  icon?: React.ReactNode;
  extra?: React.ReactNode;
}

export interface SimpleTimelineProps {
  items: SimpleTimelineItem[];
  compact?: boolean;
  animated?: boolean;
  className?: string;
}

const dotColors: Record<string, string> = {
  default: 'bg-slate-300 dark:bg-slate-600',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
  pending: 'bg-slate-400 animate-pulse',
};

const iconBgColors: Record<string, string> = {
  default: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
  success: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
  error: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  info: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
  pending: 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
};

export const SimpleTimeline: React.FC<SimpleTimelineProps> = ({
  items,
  compact = false,
  animated = false,
  className = '',
}) => (
  <div className={cn('relative', className)}>
    {items.map((item, idx) => {
      const isLast = idx === items.length - 1;
      const status = item.status || 'default';

      return (
        <div
          key={item.id}
          className={cn('relative flex gap-3', compact ? 'pb-4' : 'pb-6')}
          style={animated ? { animation: `fadeInUp 0.3s ease ${idx * 0.08}s both` } : undefined}
        >
          {/* Vertical line + dot/icon */}
          <div className="flex flex-col items-center shrink-0" style={{ width: item.icon ? 32 : 12 }}>
            {item.icon ? (
              <div className={cn('w-8 h-8 rounded-full flex items-center justify-center text-sm', iconBgColors[status])}>
                {item.icon}
              </div>
            ) : (
              <div className={cn('w-3 h-3 rounded-full mt-1.5 shrink-0 ring-4 ring-white dark:ring-slate-900', dotColors[status])} />
            )}
            {!isLast && (
              <div className="w-px flex-1 bg-slate-200 dark:bg-slate-700 mt-1" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-sm text-slate-800 dark:text-slate-200">
                {item.title}
              </div>
              {item.timestamp && (
                <span className="text-[11px] text-slate-400 dark:text-slate-500 shrink-0 font-mono">
                  {item.timestamp}
                </span>
              )}
            </div>
            {item.description && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {item.description}
              </div>
            )}
            {item.extra && <div className="mt-2">{item.extra}</div>}
          </div>
        </div>
      );
    })}
  </div>
);

// Default export — backward compatible
export default SimpleTimeline;
